import log from '@/lib/logger';
import {useState} from 'react';
import {ConfirmationConfig, I18nValues} from './confirmation-dialog';
import {CONFIRMATION_PRESETS, ConfirmationPreset} from './confirmation-presets';

// Type für die values Parameter in den Hooks
export type ConfirmationValues = {
    title?: I18nValues;
    description?: I18nValues;
    cancelAriaLabel?: I18nValues;
    confirmAriaLabel?: I18nValues;
};

export interface UseConfirmationDialogReturn {
    isOpen: boolean;
    config: ConfirmationConfig | null;
    entityId: string | undefined;
    openWithPreset: (presetName: ConfirmationPreset, entityId?: string, values?: ConfirmationValues) => void;
    openWithConfig: (customConfig: ConfirmationConfig, entityId?: string) => void;
    closeDialog: () => void;
    dialogProps: {
        isOpen: boolean;
        onCancel: () => void;
        config: ConfirmationConfig;
        entityId: string | undefined;
    } | null;
}

export function useConfirmationDialog(): UseConfirmationDialogReturn {
    const [isOpen, setIsOpen] = useState(false);
    const [config, setConfig] = useState<ConfirmationConfig | null>(null);
    const [entityId, setEntityId] = useState<string | undefined>(undefined);

    function openWithPreset(
        presetName: ConfirmationPreset,
        entityId?: string,
        values?: ConfirmationValues
    ) {
        const presetConfig = CONFIRMATION_PRESETS[presetName];
        if (!presetConfig) {
            log.error(`Preset '${presetName}' nicht gefunden`);
            return;
        }

        const configWithValues: ConfirmationConfig = {
            ...presetConfig,
            titleValues: values?.title,
            descriptionValues: values?.description,
            cancelButton: {
                ...presetConfig.cancelButton,
                ariaLabelValues: values?.cancelAriaLabel
            },
            confirmButton: {
                ...presetConfig.confirmButton,
                ariaLabelValues: values?.confirmAriaLabel
            }
        };

        setConfig(configWithValues);
        setEntityId(entityId);
        setIsOpen(true);
    }

    function openWithConfig(
        customConfig: ConfirmationConfig,
        entityId?: string
    ) {
        setConfig(customConfig);
        setEntityId(entityId);
        setIsOpen(true);
    }

    function closeDialog() {
        setIsOpen(false);
        setConfig(null);
        setEntityId(undefined);
    }

    return {
        isOpen,
        config,
        entityId,
        openWithPreset,
        openWithConfig,
        closeDialog,
        dialogProps: config ? {
            isOpen,
            onCancel: closeDialog,
            config,
            entityId
        } : null
    };
}

export function useDeleteConfirmation() {
    const confirmation = useConfirmationDialog();

    return {
        ...confirmation,
        openDeleteConfirmation: (entityId?: string, values?: ConfirmationValues) =>
            confirmation.openWithPreset('delete', entityId, values)
    };
}

export function useCancelConfirmation() {
    const confirmation = useConfirmationDialog();

    return {
        ...confirmation,
        openCancelConfirmation: (entityId?: string, values?: ConfirmationValues) =>
            confirmation.openWithPreset('cancel', entityId, values)
    };
}

export function useArchiveConfirmation() {
    const confirmation = useConfirmationDialog();

    return {
        ...confirmation,
        openArchiveConfirmation: (entityId?: string, values?: ConfirmationValues) =>
            confirmation.openWithPreset('archive', entityId, values)
    };
}

export function useDeactivateConfirmation() {
    const confirmation = useConfirmationDialog();

    return {
        ...confirmation,
        openDeactivateConfirmation: (entityId?: string, values?: ConfirmationValues) =>
            confirmation.openWithPreset('deactivate', entityId, values)
    };
}

export function useActivateConfirmation() {
    const confirmation = useConfirmationDialog();

    return {
        ...confirmation,
        openActivateConfirmation: (entityId?: string, values?: ConfirmationValues) =>
            confirmation.openWithPreset('activate', entityId, values)
    };
}

export function useExportConfirmation() {
    const confirmation = useConfirmationDialog();

    return {
        ...confirmation,
        openExportConfirmation: (entityId?: string, values?: ConfirmationValues) =>
            confirmation.openWithPreset('export', entityId, values)
    };
}

export function useResetConfirmation() {
    const confirmation = useConfirmationDialog();

    return {
        ...confirmation,
        openResetConfirmation: (entityId?: string, values?: ConfirmationValues) =>
            confirmation.openWithPreset('reset', entityId, values)
    };
}
